// ThemeProvider — runtime palette application for :root CSS variables.
//
// Responsibilities:
// - Apply a ThemePalette JSON (shape: packages/brand/src/templates/*.json) to
//   document.documentElement.style as CSS custom properties.
// - Track theme mode (light | dark | system) and persist to localStorage under
//   the key "mms-ui-prefs" using the Customer UI Preferences shape defined in
//   docs/steering/vi-spec.md.
// - Resolve "system" through matchMedia("(prefers-color-scheme: dark)").
// - Support cross-tab sync via the native `storage` event.
// - Provide a small subscribe/notify observer API for Vue composables and other
//   consumers.
//
// Design notes:
// - No runtime dependencies. Pure DOM + Web Storage.
// - Fetchers (fetchActivePalette, fetchCustomerPrefs) are injected at construction
//   so this package stays transport-agnostic. API URLs live in the consumer.
// - Template JSON stores 3-component OKLCH (e.g. "oklch(0.96 0.003 250)"), but
//   bundled default-{dark,light}.css emit --border-* tokens WITH alpha. We wrap
//   border tokens in an alpha channel here so runtime-applied templates match
//   the bundled baseline visually (Option A in the task brief).

const LS_KEY = "mms-ui-prefs";

// Palette path -> CSS custom property. 18 vars per mode.
// Keep in sync with docs/steering/vi-spec.md § CSS Variable Mapping.
const CSS_VAR_MAP = {
  "brand.primary":           "--brand-primary",
  "brand.pillar_artist":     "--brand-pillar-artist",
  "brand.pillar_everywhere": "--brand-pillar-everywhere",
  "brand.pillar_share":      "--brand-pillar-share",
  "surface.bg":              "--surface-bg",
  "surface.s1":              "--surface-s1",
  "surface.s2":              "--surface-s2",
  "surface.s3":              "--surface-s3",
  "text.primary":            "--text-primary",
  "text.secondary":          "--text-secondary",
  "text.muted":              "--text-muted",
  "border.default":          "--border-default",
  "border.subtle":           "--border-subtle",
  "border.strong":           "--border-strong",
  "state.success":           "--state-success",
  "state.warning":           "--state-warning",
  "state.danger":            "--state-danger",
  "state.info":              "--state-info",
};

// Alpha defaults for border tokens. Values match default-dark.css / default-light.css
// exactly so that runtime-applied template palettes are visually consistent with
// the bundled baseline CSS.
const BORDER_ALPHA = {
  dark:  { default: 0.10, subtle: 0.06, strong: 0.18 },
  light: { default: 0.12, subtle: 0.06, strong: 0.22 },
};

function readPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

// Splice an alpha channel into a 3-component oklch() string.
// "oklch(0.96 0.003 250)" -> "oklch(0.96 0.003 250 / 0.10)"
// If the input already has an alpha channel (contains "/") it is returned as-is
// so customer-overridden values are preserved.
/**
 * Provides the withAlpha operation.
 * @param oklchStr The input value.
 * @param alpha The input value.
 * @returns The operation result.
 */
export function withAlpha(oklchStr, alpha) {
  if (typeof oklchStr !== "string") return oklchStr;
  if (oklchStr.includes("/")) return oklchStr;
  const close = oklchStr.lastIndexOf(")");
  if (close === -1) return oklchStr;
  const body = oklchStr.slice(0, close).trimEnd();
  return `${body} / ${alpha})`;
}

// Safe localStorage JSON accessors. Guards against:
// - SSR / non-browser contexts where `localStorage` is undefined
// - Malformed JSON from a stale schema
// - Quota exceeded errors on write
function loadPrefs() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : null;
  } catch {
    return null;
  }
}

function savePrefs(prefs) {
  try {
    if (typeof localStorage === "undefined") return;
    const payload = { ...prefs, updated_at: new Date().toISOString() };
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded or blocked storage — swallow. Preferences are best-effort.
  }
}

/**
 * Provides the ThemeProvider behavior.
 */
export class ThemeProvider {
  constructor({ fetchActivePalette = null, fetchCustomerPrefs = null } = {}) {
    this.fetchActivePalette = fetchActivePalette;
    this.fetchCustomerPrefs = fetchCustomerPrefs;
    this.palette = null;
    this.mode = "system";
    this.subscribers = new Set();
    this.storageListener = null;
  }

  // Boot sequence (call once at app startup):
  //   1. Load local preferences and set mode (no network).
  //   2. If local prefs carry a cached image palette, use it.
  //   3. Else try fetchCustomerPrefs() for authenticated users.
  //   4. Else fall back to fetchActivePalette() for the brand default.
  //   5. Apply whatever we resolved and install the cross-tab storage listener.
  async boot() {
    const localPrefs = loadPrefs();
    const mode = localPrefs?.theme_mode ?? "system";
    this.setMode(mode);

    let palette = null;
    if (localPrefs?.palette_kind === "image" && localPrefs.palette_json) {
      palette = localPrefs.palette_json;
    }

    if (!palette && this.fetchCustomerPrefs) {
      try {
        const remote = await this.fetchCustomerPrefs();
        if (remote?.palette_json) palette = remote.palette_json;
        if (remote?.theme_mode) this.setMode(remote.theme_mode);
      } catch {
        // Network failure is non-fatal — brand default below.
      }
    }

    if (!palette && this.fetchActivePalette) {
      try {
        palette = await this.fetchActivePalette();
      } catch {
        // Non-fatal.
      }
    }

    if (palette) this.applyPalette(palette, this.resolveMode());
    this.installStorageListener();
    return palette;
  }

  // Map the user-facing mode preference ("system") onto an OS-resolved value.
  resolveMode() {
    if (this.mode === "system") {
      if (typeof window === "undefined" || !window.matchMedia) return "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return this.mode;
  }

  // Stored preference keeps "system" intact; resolved value is what we stamp onto
  // <html data-theme="..."> so CSS selectors can react immediately.
  setMode(mode) {
    // No-op guard: if the mode is unchanged AND the DOM already reflects the
    // correct resolved theme, skip. Prevents churn from redundant calls and
    // avoids feedback loops when a cross-tab storage handler reconciles state.
    const currentResolved = this.resolveMode();
    if (
      mode === this.mode &&
      typeof document !== "undefined" &&
      document.documentElement &&
      document.documentElement.dataset.theme === currentResolved
    ) {
      return;
    }
    this.mode = mode;
    const resolved = this.resolveMode();
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.dataset.theme = resolved;
      document.documentElement.style.colorScheme = resolved;
    }
    // Only persist when the stored mode actually differs — avoids a needless
    // localStorage write that would fire a `storage` event in other tabs.
    const prefs = loadPrefs() ?? {};
    if (prefs.theme_mode !== mode) {
      savePrefs({ ...prefs, theme_mode: mode });
    }
    if (this.palette) this.applyPalette(this.palette, resolved);
    this.notify();
  }

  // Write palette tokens to :root as CSS custom properties. Border tokens are
  // wrapped with the mode-specific default alpha (see BORDER_ALPHA) so they
  // match bundled default-*.css visually.
  applyPalette(palette, resolvedMode) {
    this.palette = palette;
    const tokens = palette?.modes?.[resolvedMode];
    if (!tokens || typeof document === "undefined" || !document.documentElement) {
      this.notify();
      return;
    }
    const root = document.documentElement.style;
    const alphaMap = BORDER_ALPHA[resolvedMode] ?? null;

    for (const [path, cssVar] of Object.entries(CSS_VAR_MAP)) {
      const value = readPath(tokens, path);
      if (value == null) continue;
      let emitted = value;
      if (path.startsWith("border.") && alphaMap) {
        const leaf = path.slice("border.".length); // "default" | "subtle" | "strong" | ...
        const alpha = alphaMap[leaf];
        if (typeof alpha === "number") emitted = withAlpha(value, alpha);
      }
      root.setProperty(cssVar, emitted);
    }
    this.notify();
  }

  // Customer picks an image-derived palette. Persist as kind="image" with the
  // full snapshot so it survives the source image being deleted later.
  pickImagePalette(paletteJson) {
    const prefs = loadPrefs() ?? {};
    savePrefs({
      ...prefs,
      palette_kind: "image",
      palette_ref: paletteJson?.source?.image_id ?? null,
      palette_json: paletteJson,
    });
    this.applyPalette(paletteJson, this.resolveMode());
  }

  // Customer resets to the current brand default. Per schemas.md § Storage Rules,
  // "brand" kind keeps palette_json null and resolves at read time.
  // Returns the applied palette (or null on failure / no fetcher) so callers
  // can await the full reset including the brand re-fetch.
  async resetToBrand() {
    const prefs = loadPrefs() ?? {};
    savePrefs({
      ...prefs,
      palette_kind: "brand",
      palette_ref: null,
      palette_json: null,
    });
    if (!this.fetchActivePalette) return null;
    try {
      const palette = await this.fetchActivePalette();
      if (palette) this.applyPalette(palette, this.resolveMode());
      return palette ?? null;
    } catch {
      return null;
    }
  }

  subscribe(cb) {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  notify() {
    const payload = { palette: this.palette, mode: this.resolveMode() };
    for (const cb of this.subscribers) {
      try { cb(payload); } catch { /* subscriber errors shouldn't break provider */ }
    }
  }

  // Cross-tab sync. The native `storage` event fires in every OTHER tab when
  // localStorage changes in one tab. We reconcile mode + palette accordingly.
  installStorageListener() {
    if (typeof window === "undefined") return;
    // Idempotent: remove any prior listener so multiple boots don't stack.
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
    }
    this.storageListener = (e) => {
      if (e.key !== LS_KEY) return;
      const prefs = loadPrefs();
      if (!prefs) return;

      // Mode change: update state + DOM directly. We must NOT call setMode
      // here — setMode would re-save to localStorage and fire another
      // `storage` event, creating a cross-tab feedback loop.
      if (prefs.theme_mode && prefs.theme_mode !== this.mode) {
        this.mode = prefs.theme_mode;
        const resolved = this.resolveMode();
        if (typeof document !== "undefined" && document.documentElement) {
          document.documentElement.dataset.theme = resolved;
          document.documentElement.style.colorScheme = resolved;
        }
      }

      // Palette change: only re-apply when the JSON reference actually differs.
      // Avoids 18 redundant CSS var writes on every unrelated prefs update
      // (e.g. a mode-only toggle in another tab).
      if (prefs.palette_json && prefs.palette_json !== this.palette) {
        this.applyPalette(prefs.palette_json, this.resolveMode());
        // applyPalette already calls notify(); nothing more to do.
        return;
      }

      this.notify();
    };
    window.addEventListener("storage", this.storageListener);
  }
}

// Exported for tests and downstream tooling (e.g., an admin debug panel).
export { LS_KEY, CSS_VAR_MAP, BORDER_ALPHA };
